import { createAccountSetupLink, router } from "@/router";
import { ApiClient, TokenAdd } from "@/services/apiClient";
import { afterLogin } from "@/services/user/auth/login/afterLoginService";
import { setupUserInApp } from "@/services/user/auth/login/helpers";
import { TokenService } from "@/services/user/auth/login/tokenService";
import type { User } from "@/services/user/userTypes";
import { AuthenticationException, TokenNotValidException, UserNotEstablishedException } from "@/services/user/userExceptions";
import { AxiosError } from "axios";

export type AuthenticationPromise = Promise<void> | null;

type Credentials = { email: string; password: string } | { token: string };

let promise: AuthenticationPromise = null;

const isInProgressOrDone = (): boolean => {
  return promise !== null;
};

const run = async (credentials: Credentials): Promise<AuthenticationPromise> => {
  if (!isInProgressOrDone()) {
    promise = createAuthenticationPromise(credentials);
  }

  return promise;
};

const reset = (): void => {
  promise = null;
};

const resolveManually = () => {
  promise = new Promise((resolve) => resolve());
};

const createAuthenticationPromise = (credentials: Credentials): AuthenticationPromise => {
  const authUrl = "user/authenticate";
  return ApiClient.postPublic<User>(authUrl, {}, credentials, TokenAdd.Never)
    .then(({ data: user }) => setupUserInApp(user))
    .then(afterLogin)
    .catch(handleAuthenticationError);
};

const handleAuthenticationError = (error: unknown): void => {
  TokenService.delete();

  if (error instanceof UserNotEstablishedException) {
    const redirect = createAccountSetupLink();
    router.push(redirect);
  } else if (error instanceof AxiosError && error.response?.status === 400) {
    const errorCode = error.response?.data.status;
    const TokenNotValidStatusCode = 3;
    if (errorCode === TokenNotValidStatusCode) {
      // We don't want any message here.
      throw new TokenNotValidException();
    } else {
      const msg = error.response?.data.message;
      throw new AuthenticationException(msg);
    }
  } else {
    throw error;
  }
};

export const Authentication = {
  promise,

  isInProgressOrDone,

  run,
  reset,
  resolveManually,
};
